<template>
  <div>
    <!-- Sección 1: Upload CSV -->
    <b-card no-body class="mb-1">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div>
            <h4 class="mb-0" style="font-weight: 600">Importar Factura UPS</h4>
            <small class="text-muted">Sube el CSV de facturación de UPS</small>
          </div>
          <b-badge variant="light-primary" pill>CSV</b-badge>
        </div>
        <div class="upload-zone" @dragover.prevent @drop.prevent>
          <b-row class="align-items-center">
            <b-col cols="9" md="10">
              <b-form-file
                v-model="csvInput"
                placeholder="Arrastra o selecciona archivos CSV..."
                drop-placeholder="Suelta aquí..."
                accept=".csv"
                multiple
              />
            </b-col>
            <b-col cols="3" md="2">
              <b-button
                variant="primary"
                block
                @click="procesarCSV"
                :disabled="procesando || !csvInput || (Array.isArray(csvInput) && csvInput.length === 0)"
              >
                <span v-if="procesando"><i class="fa-solid fa-spinner fa-spin"></i> Subiendo</span>
                <span v-else><i class="fa-solid fa-cloud-arrow-up"></i> Procesar</span>
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>

    <!-- Sección 2: Lista de facturas -->
    <b-card no-body class="mb-1">
      <b-card-header
        class="d-flex align-items-center justify-content-between cursor-pointer py-1 px-2"
        @click="mostrarFacturas = !mostrarFacturas"
      >
        <div class="d-flex align-items-center">
          <i :class="mostrarFacturas ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="mr-75" style="font-size: 12px; transition: transform 0.2s"></i>
          <div>
            <h4 class="mb-0" style="font-weight: 600">Facturas Importadas</h4>
            <small class="text-muted">{{ facturas.length }} factura{{ facturas.length !== 1 ? 's' : '' }}</small>
          </div>
        </div>
        <b-badge v-if="totalImporte > 0" variant="light-warning" pill style="font-size: 14px">
          Total: {{ totalImporte }} €
        </b-badge>
      </b-card-header>
      <b-collapse v-model="mostrarFacturas">
        <b-card-body class="pt-0">
          <b-table
            :items="facturas"
            :fields="columnasFacturas"
            hover
            responsive
            show-empty
            empty-text="No hay facturas importadas"
            thead-class="table-header-custom"
            class="tabla-facturas"
          >
            <template #cell(numeroFactura)="data">
              <span style="font-weight: 600">{{ data.value }}</span>
            </template>
            <template #cell(importeTotal)="data">
              <b-badge variant="light-success" pill style="font-size: 13px">
                {{ parseFloat(data.value).toFixed(2) }} €
              </b-badge>
            </template>
            <template #cell(moneda)="data">
              <b-badge variant="light-secondary" pill>{{ data.value }}</b-badge>
            </template>
            <template #cell(fechaFactura)="data">
              {{ formatFecha(data.value) }}
            </template>
            <template #cell(fechaVencimiento)="data">
              {{ formatFecha(data.value) }}
            </template>
            <template #cell(acciones)="data">
              <b-button size="sm" variant="flat-danger" @click="confirmarEliminar(data.item)" class="btn-icon">
                <i class="fa-regular fa-trash-can"></i>
              </b-button>
            </template>
          </b-table>
        </b-card-body>
      </b-collapse>
    </b-card>

    <!-- Modal líneas -->
    <b-modal
      id="modal-lineas"
      centered
      size="xl"
      hide-footer
      scrollable
      header-bg-variant="white"
    >
      <template #modal-title>
        <div>
          <span style="font-weight: 600">Factura {{ facturaSeleccionada.numeroFactura }}</span>
          <b-badge variant="light-primary" pill class="ml-1">{{ lineasFactura.length }} líneas</b-badge>
        </div>
      </template>

      <div v-if="lineasFactura.length === 0" class="text-center py-3">
        <i class="fa-solid fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-1 text-muted">Cargando líneas...</p>
      </div>

      <!-- Resumen rápido del modal -->
      <b-row v-if="lineasFactura.length > 0" class="mb-1">
        <b-col cols="3">
          <div class="resumen-card bg-light-primary">
            <small class="text-muted">Envíos</small>
            <h5 class="mb-0">{{ resumenLineas.envios }}</h5>
          </div>
        </b-col>
        <b-col cols="3">
          <div class="resumen-card bg-light-success">
            <small class="text-muted">Devoluciones</small>
            <h5 class="mb-0">{{ resumenLineas.devoluciones }}</h5>
          </div>
        </b-col>
        <b-col cols="3">
          <div class="resumen-card bg-light-warning">
            <small class="text-muted">Países</small>
            <h5 class="mb-0">{{ resumenLineas.paises }}</h5>
          </div>
        </b-col>
        <b-col cols="3">
          <div class="resumen-card bg-light-danger">
            <small class="text-muted">Total Neto</small>
            <h5 class="mb-0">{{ resumenLineas.totalNeto }} €</h5>
          </div>
        </b-col>
      </b-row>

      <b-table
        v-if="lineasFactura.length > 0"
        :items="lineasFactura"
        :fields="columnasLineas"
        hover
        responsive
        small
        thead-class="table-header-custom"
      >
        <template #cell(tipoEnvio)="data">
          <b-badge :variant="data.value === 'envio' ? 'light-primary' : 'light-warning'" pill>
            <i :class="data.value === 'envio' ? 'fa-solid fa-arrow-up-right-from-square' : 'fa-solid fa-rotate-left'"></i>
            {{ data.value === 'envio' ? 'Envío' : 'Devolución' }}
          </b-badge>
        </template>
        <template #cell(cantidadNeta)="data">
          <span :style="{ color: parseFloat(data.value) < 0 ? '#ea5455' : '#28c76f', fontWeight: 600 }">
            {{ parseFloat(data.value).toFixed(2) }} €
          </span>
        </template>
        <template #cell(valorBase)="data">
          {{ parseFloat(data.value).toFixed(2) }}
        </template>
        <template #cell(cantidadIncentivo)="data">
          {{ parseFloat(data.value).toFixed(2) }}
        </template>
        <template #cell(paisDestino)="data">
          <span v-if="data.value">
            {{ data.value }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
      </b-table>
    </b-modal>

    <!-- Sección 3: Análisis -->
    <b-card no-body class="mb-1">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div>
            <h4 class="mb-0" style="font-weight: 600">Análisis de Costes</h4>
            <small class="text-muted">Analiza los gastos de envío UPS por tipo, zona o periodo</small>
          </div>
          <b-badge variant="light-info" pill>
            <i class="fa-solid fa-chart-line"></i> Analytics
          </b-badge>
        </div>

        <div class="d-flex align-items-end" style="gap: 12px; flex-wrap: wrap">
          <div style="min-width: 250px; flex: 2">
            <label class="label-custom">Tipo de análisis</label>
            <v-select
              v-model="analisisTipo"
              :options="tiposAnalisis"
              :clearable="false"
              :reduce="opt => opt.value"
              label="text"
              :appendToBody="true"
            />
          </div>
          <div style="min-width: 180px; flex: 1">
            <label class="label-custom">Factura</label>
            <v-select
              v-model="analisisFacturaId"
              :options="opcionesFacturas"
              :reduce="opt => opt.value"
              label="text"
              placeholder="Todas"
              :appendToBody="true"
            />
          </div>
          <div style="min-width: 140px; flex: 1">
            <label class="label-custom">Desde</label>
            <b-form-input type="date" v-model="analisisDesde" />
          </div>
          <div style="min-width: 140px; flex: 1">
            <label class="label-custom">Hasta</label>
            <b-form-input type="date" v-model="analisisHasta" />
          </div>
          <div>
            <b-button variant="primary" @click="ejecutarAnalisis" :disabled="analizando" style="white-space: nowrap">
              <span v-if="analizando"><i class="fa-solid fa-spinner fa-spin"></i> Analizando</span>
              <span v-else><i class="fa-solid fa-magnifying-glass-chart"></i> Analizar</span>
            </b-button>
          </div>
        </div>

        <hr class="my-2" />

        <!-- Resumen análisis -->
        <div v-if="analisisResultados.length > 0">
          <!-- Resumen temporal -->
          <b-row v-if="analisisTipo === 'temporal'" class="mb-1">
            <b-col cols="4">
              <div class="resumen-card bg-light-primary">
                <small class="text-muted">Coste Total</small>
                <h5 class="mb-0">{{ resumenAnalisis.costeTotal }} €</h5>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="resumen-card bg-light-info">
                <small class="text-muted">Total Envíos</small>
                <h5 class="mb-0">{{ resumenAnalisis.totalEnvios }}</h5>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="resumen-card bg-light-warning">
                <small class="text-muted">Media/Mes</small>
                <h5 class="mb-0">{{ resumenAnalisis.mediaMes }} €</h5>
              </div>
            </b-col>
          </b-row>

          <!-- Resumen zona -->
          <b-row v-if="analisisTipo === 'zona'" class="mb-1">
            <b-col cols="3">
              <div class="resumen-card bg-light-primary">
                <small class="text-muted">Total Líneas</small>
                <h5 class="mb-0">{{ resumenZona.totalCoste }} €</h5>
              </div>
            </b-col>
            <b-col cols="3">
              <div class="resumen-card bg-light-success">
                <small class="text-muted">Envíos</small>
                <h5 class="mb-0">{{ resumenZona.costeEnvios }} €</h5>
              </div>
            </b-col>
            <b-col cols="3">
              <div class="resumen-card bg-light-warning">
                <small class="text-muted">Devoluciones</small>
                <h5 class="mb-0">{{ resumenZona.costeDevoluciones }} €</h5>
              </div>
            </b-col>
            <b-col cols="3">
              <div class="resumen-card bg-light-danger">
                <small class="text-muted">Sin asignar</small>
                <h5 class="mb-0">{{ resumenZona.costeSinAsignar }} €</h5>
              </div>
            </b-col>
          </b-row>

          <!-- Resumen categoría -->
          <b-row v-if="analisisTipo === 'categoria'" class="mb-1">
            <b-col cols="4">
              <div class="resumen-card bg-light-primary">
                <small class="text-muted">Coste Total</small>
                <h5 class="mb-0">{{ resumenCategoria.totalCoste }} €</h5>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="resumen-card bg-light-info">
                <small class="text-muted">Total Envíos</small>
                <h5 class="mb-0">{{ totalEnviosUnicos }}</h5>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="resumen-card bg-light-warning">
                <small class="text-muted">Categorías</small>
                <h5 class="mb-0">{{ resumenCategoria.numCategorias }}</h5>
              </div>
            </b-col>
          </b-row>

          <!-- Chart categoría -->
          <div v-if="analisisTipo === 'categoria' && chartCategoriaSeries.length > 0" class="chart-container mb-2">
            <b-row>
              <b-col cols="12" md="6">
                <apexchart
                  type="bar"
                  height="350"
                  :options="chartCategoriaOptions"
                  :series="chartCategoriaSeries"
                />
              </b-col>
              <b-col cols="12" md="6">
                <apexchart
                  type="donut"
                  height="350"
                  :options="chartCategoriaDonutOptions"
                  :series="chartCategoriaDonutSeries"
                />
              </b-col>
            </b-row>
          </div>

          <!-- Chart temporal -->
          <div v-if="analisisTipo === 'temporal'" class="chart-container mb-2">
            <apexchart
              type="area"
              height="350"
              :options="chartOptions"
              :series="chartSeries"
            />
          </div>

          <!-- Chart zona/país -->
          <div v-if="analisisTipo === 'zona' && chartZonaSeries.length > 0" class="chart-container mb-2">
            <b-row>
              <b-col cols="12" md="6">
                <apexchart
                  type="bar"
                  height="350"
                  :options="chartZonaOptions"
                  :series="chartZonaSeries"
                />
              </b-col>
              <b-col cols="12" md="6">
                <apexchart
                  type="donut"
                  height="350"
                  :options="chartZonaDonutOptions"
                  :series="chartZonaDonutSeries"
                />
              </b-col>
            </b-row>
          </div>

          <b-table
            :items="analisisResultados"
            :fields="columnasAnalisis"
            hover
            responsive
            small
            thead-class="table-header-custom"
            class="tabla-facturas"
            @row-clicked="onRowClick"
            :tbody-tr-class="rowClass"
          >
            <template #cell(costeTotal)="data">
              <span style="font-weight: 600; color: #7367f0">{{ parseFloat(data.value).toFixed(2) }} €</span>
            </template>
            <template #cell(costeMedio)="data">
              {{ parseFloat(data.value).toFixed(2) }} €
            </template>
            <template #cell(costeFlete)="data">
              {{ parseFloat(data.value).toFixed(2) }} €
            </template>
            <template #cell(costeCombustible)="data">
              {{ parseFloat(data.value).toFixed(2) }} €
            </template>
            <template #cell(costeAccesorios)="data">
              {{ parseFloat(data.value).toFixed(2) }} €
            </template>
            <template #cell(costeImpuestos)="data">
              {{ parseFloat(data.value).toFixed(2) }} €
            </template>
            <template #cell(paisDestino)="data">
              <span v-if="data.value" :style="analisisTipo === 'zona' ? 'cursor: pointer' : ''">
                <i v-if="analisisTipo === 'zona'" :class="data.item._showDetails ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="fa-xs text-muted mr-50"></i>
                {{ data.value }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(tipoEnvio)="data">
              <b-badge :variant="data.value === 'envio' ? 'light-primary' : 'light-warning'" pill>
                <i :class="data.value === 'envio' ? 'fa-solid fa-arrow-up-right-from-square' : 'fa-solid fa-rotate-left'"></i>
                {{ data.value === 'envio' ? 'Envío' : 'Devolución' }}
              </b-badge>
            </template>
            <template #cell(clasificacionCargo)="data">
              <b-badge :variant="varianteCargo(data.value)" pill>{{ data.value }}</b-badge>
            </template>
            <template #cell(mes)="data">
              <span style="font-weight: 600">{{ data.value }}</span>
            </template>

            <template #row-details="row">
              <div v-if="row.item._loading" class="text-center py-1">
                <i class="fa-solid fa-spinner fa-spin text-primary"></i>
              </div>
              <div v-else class="desglose-container">
                <div v-for="(cat, idx) in row.item._categorias" :key="cat.clasificacionCargo + cat.descripcionCargo">
                  <div class="desglose-row" :class="{ 'cursor-pointer': true, 'desglose-row-active': cat._open }" @click="toggleDetalle(row.item, cat, idx)">
                    <div class="desglose-badge">
                      <b-badge :variant="varianteCargo(cat.clasificacionCargo)" pill>{{ cat.clasificacionCargo }}</b-badge>
                    </div>
                    <div class="desglose-desc">
                      <i :class="cat._open ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'" class="fa-xs text-muted mr-50"></i>
                      {{ cat.descripcionCargo }}
                    </div>
                    <div class="desglose-envios">{{ cat.numEnvios }} líneas</div>
                    <div class="desglose-coste">{{ parseFloat(cat.costeTotal).toFixed(2) }} €</div>
                  </div>
                  <!-- Trackings de esta categoría -->
                  <div v-if="cat._open" class="detalle-trackings">
                    <div v-if="cat._loadingDetalle" class="text-center py-1">
                      <i class="fa-solid fa-spinner fa-spin text-primary fa-sm"></i>
                    </div>
                    <div v-else-if="cat._trackings && cat._trackings.length > 0">
                      <div v-for="t in cat._trackings" :key="t.numeroSeguimiento + t.cantidadNeta" class="detalle-tracking-row">
                        <div class="detalle-tracking-num">{{ t.numeroSeguimiento }}</div>
                        <div class="detalle-tracking-ref">{{ t.referenciaEnvio }}</div>
                        <div class="detalle-tracking-fecha">{{ formatFecha(t.fechaTransaccion) }}</div>
                        <div class="detalle-tracking-peso">{{ t.pesoFacturado }} kg</div>
                        <div class="detalle-tracking-coste">{{ parseFloat(t.cantidadNeta).toFixed(2) }} €</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </b-table>
        </div>

        <div v-if="analisisResultados.length === 0 && !analizando" class="text-center py-3">
          <i class="fa-solid fa-chart-pie fa-3x text-muted mb-1" style="opacity: 0.3"></i>
          <p class="text-muted mb-0">Selecciona un tipo de análisis y pulsa Analizar</p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { BRow, BCol, BFormFile, BButton, BTable, BFormInput, BCard, BCardBody, BCardHeader, BBadge, BCollapse } from 'bootstrap-vue'
import Papa from 'papaparse'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    BRow,
    BCol,
    BFormFile,
    BButton,
    BTable,
    BFormInput,
    BCard,
    BCardBody,
    BCardHeader,
    BBadge,
    BCollapse,
    vSelect,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      csvInput: null,
      procesando: false,
      mostrarFacturas: false,
      facturas: [],
      facturaSeleccionada: {},
      lineasFactura: [],
      columnasFacturas: [
        { key: 'numeroFactura', label: 'Nº Factura', sortable: true },
        { key: 'fechaFactura', label: 'Fecha', sortable: true },
        { key: 'moneda', label: 'Moneda', sortable: true },
        { key: 'importeTotal', label: 'Importe', sortable: true },
        { key: 'numeroCuenta', label: 'Cuenta', sortable: true },
        { key: 'fechaVencimiento', label: 'Vencimiento', sortable: true },
        { key: 'acciones', label: '', sortable: false, thStyle: { width: '100px' } },
      ],
      columnasLineas: [
        { key: 'tipoEnvio', label: 'Tipo' },
        { key: 'fechaTransaccion', label: 'Fecha' },
        { key: 'numeroSeguimiento', label: 'Tracking' },
        { key: 'referenciaEnvio', label: 'Referencia' },
        { key: 'paisDestino', label: 'País' },
        { key: 'zona', label: 'Zona' },
        { key: 'descripcionCargo', label: 'Descripción' },
        { key: 'clasificacionCargo', label: 'Clasif.' },
        { key: 'pesoFacturado', label: 'Peso' },
        { key: 'valorBase', label: 'Base' },
        { key: 'cantidadIncentivo', label: 'Incentivo' },
        { key: 'cantidadNeta', label: 'Neto' },
      ],
      // Análisis
      analisisTipo: 'zona',
      analisisFacturaId: null,
      analisisDesde: '',
      analisisHasta: '',
      analizando: false,
      analisisResultados: [],
      tiposAnalisis: [
        { text: 'Temporal (por mes)', value: 'temporal' },
        { text: 'Por envío', value: 'envio' },
        { text: 'Por zona/país', value: 'zona' },
        { text: 'Por categoría', value: 'categoria' },
      ],
      chartOptions: {},
      chartSeries: [],
      chartZonaOptions: {},
      chartZonaSeries: [],
      chartZonaDonutOptions: {},
      chartZonaDonutSeries: [],
      chartCategoriaOptions: {},
      chartCategoriaSeries: [],
      chartCategoriaDonutOptions: {},
      chartCategoriaDonutSeries: [],
      totalEnviosUnicos: 0,
    }
  },
  computed: {
    opcionesFacturas() {
      return this.facturas.map(function(f) {
        return { text: f.numeroFactura + ' (' + parseFloat(f.importeTotal).toFixed(2) + '€)', value: f.id }
      })
    },
    totalImporte() {
      var total = 0
      this.facturas.forEach(function(f) { total += parseFloat(f.importeTotal) || 0 })
      return total.toFixed(2)
    },
    resumenLineas() {
      var envios = 0
      var devoluciones = 0
      var paisesSet = {}
      var totalNeto = 0
      this.lineasFactura.forEach(function(l) {
        if (l.tipoEnvio === 'envio') envios++
        else devoluciones++
        if (l.paisDestino) paisesSet[l.paisDestino] = true
        totalNeto += parseFloat(l.cantidadNeta) || 0
      })
      return {
        envios: envios,
        devoluciones: devoluciones,
        paises: Object.keys(paisesSet).length,
        totalNeto: totalNeto.toFixed(2)
      }
    },
    resumenZona() {
      var totalCoste = 0
      var costeEnvios = 0
      var costeDevoluciones = 0
      var costeSinAsignar = 0
      this.analisisResultados.forEach(function(r) {
        var coste = parseFloat(r.costeTotal) || 0
        totalCoste += coste
        if (r.paisDestino === 'Sin asignar') costeSinAsignar += coste
        else if (r.tipoEnvio === 'envio') costeEnvios += coste
        else costeDevoluciones += coste
      })
      return {
        totalCoste: totalCoste.toFixed(2),
        costeEnvios: costeEnvios.toFixed(2),
        costeDevoluciones: costeDevoluciones.toFixed(2),
        costeSinAsignar: costeSinAsignar.toFixed(2)
      }
    },
    resumenAnalisis() {
      var costeTotal = 0
      var totalEnvios = 0
      this.analisisResultados.forEach(function(r) {
        costeTotal += parseFloat(r.costeTotal) || 0
        totalEnvios += parseInt(r.numEnvios) || 0
      })
      var mediaMes = this.analisisResultados.length > 0 ? (costeTotal / this.analisisResultados.length) : 0
      return {
        costeTotal: costeTotal.toFixed(2),
        totalEnvios: totalEnvios,
        mediaMes: mediaMes.toFixed(2)
      }
    },
    resumenCategoria() {
      var totalCoste = 0
      var numCategorias = this.analisisResultados.length
      this.analisisResultados.forEach(function(r) {
        totalCoste += parseFloat(r.costeTotal) || 0
      })
      return {
        totalCoste: totalCoste.toFixed(2),
        numCategorias: numCategorias
      }
    },
    columnasAnalisis() {
      if (this.analisisTipo === 'envio') {
        return [
          { key: 'numeroSeguimiento', label: 'Tracking', sortable: true },
          { key: 'referenciaEnvio', label: 'Referencia', sortable: true },
          { key: 'paisDestino', label: 'País', sortable: true },
          { key: 'zona', label: 'Zona', sortable: true },
          { key: 'costeTotal', label: 'Coste Total', sortable: true },
          { key: 'fecha', label: 'Fecha', sortable: true },
        ]
      }
      if (this.analisisTipo === 'zona') {
        return [
          { key: 'paisDestino', label: 'País', sortable: true },
          { key: 'tipoEnvio', label: 'Tipo', sortable: true },
          { key: 'numEnvios', label: 'Nº Envíos', sortable: true },
          { key: 'costeTotal', label: 'Coste Total', sortable: true },
          { key: 'costeMedio', label: 'Coste Medio', sortable: true },
        ]
      }
      if (this.analisisTipo === 'categoria') {
        return [
          { key: 'clasificacionCargo', label: 'Código', sortable: true },
          { key: 'descripcionCargo', label: 'Descripción', sortable: true },
          { key: 'numEnvios', label: 'Nº Envíos', sortable: true },
          { key: 'costeTotal', label: 'Coste Total', sortable: true },
          { key: 'costeMedio', label: 'Coste Medio', sortable: true },
        ]
      }
      // temporal
      return [
        { key: 'mes', label: 'Mes', sortable: true },
        { key: 'numEnvios', label: 'Nº Envíos', sortable: true },
        { key: 'costeTotal', label: 'Coste Total', sortable: true },
        { key: 'costeFlete', label: 'Flete', sortable: true },
        { key: 'costeCombustible', label: 'Combustible', sortable: true },
        { key: 'costeAccesorios', label: 'Accesorios', sortable: true },
        { key: 'costeImpuestos', label: 'Impuestos', sortable: true },
      ]
    },
  },
  watch: {
    analisisTipo() {
      this.analisisResultados = []
      this.chartSeries = []
      this.chartOptions = {}
      this.chartZonaSeries = []
      this.chartZonaDonutSeries = []
      this.chartCategoriaSeries = []
      this.chartCategoriaDonutSeries = []
    },
    analisisFacturaId() {
      this.analisisResultados = []
    },
  },
  mounted() {
    axios.post("/uso", { seccion: "ups-facturas" })
    this.cargarFacturas()
  },
  methods: {
    async procesarCSV() {
      if (!this.csvInput || (Array.isArray(this.csvInput) && this.csvInput.length === 0)) {
        this.$bvToast.toast('Selecciona al menos un archivo CSV', {
          title: 'UPS Facturas',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.procesando = true
      var archivos = Array.isArray(this.csvInput) ? Array.from(this.csvInput) : [this.csvInput]
      var total = archivos.length
      var exitos = 0
      var errores = 0

      for (var i = 0; i < archivos.length; i++) {
        try {
          var csvText = await this.leerArchivo(archivos[i])

          var parsedData = Papa.parse(csvText, {
            delimiter: ',',
            quoteChar: '"',
            header: false,
            skipEmptyLines: true,
          }).data

          // Quitar fila de cabecera solo si la primera fila no tiene datos numéricos
          if (parsedData.length > 0 && isNaN(parseFloat(parsedData[0][10]))) parsedData.shift()

          var lineas = parsedData

          var response = await axios.post('/ups/facturas', { lineas: lineas })
          this.$bvToast.toast(response.data.msg, {
            title: 'UPS Facturas (' + (i + 1) + '/' + total + ')',
            variant: 'success',
            solid: true,
          })
          exitos++
        } catch (err) {
          errores++
          var msg = (err.response && err.response.data && err.response.data.msg) || 'Error al procesar ' + archivos[i].name
          this.$bvToast.toast(msg, {
            title: 'UPS Facturas',
            variant: 'danger',
            solid: true,
          })
        }
      }

      this.csvInput = null
      this.cargarFacturas()
      this.procesando = false
    },
    leerArchivo(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader()
        reader.onload = function(e) { resolve(reader.result) }
        reader.onerror = function(e) { reject(e) }
        reader.readAsText(file)
      })
    },
    cargarFacturas() {
      axios
        .get('/ups/facturas')
        .then(function(response) { return response.data })
        .then((res) => {
          this.facturas = res
        })
        .catch(function() {})
    },
    verLineas(factura) {
      this.facturaSeleccionada = factura
      this.lineasFactura = []
      this.$bvModal.show('modal-lineas')

      axios
        .get('/ups/facturas/' + factura.id)
        .then(function(response) { return response.data })
        .then((res) => {
          this.lineasFactura = res
        })
    },
    confirmarEliminar(factura) {
      this.$bvModal.msgBoxConfirm(
        'Eliminar factura ' + factura.numeroFactura + ' y todas sus líneas?',
        {
          title: 'Confirmar eliminación',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          centered: true,
        }
      ).then((confirmed) => {
        if (confirmed) {
          this.eliminarFactura(factura)
        }
      })
    },
    eliminarFactura(factura) {
      axios
        .delete('/ups/facturas/' + factura.id)
        .then(function(response) { return response.data })
        .then((res) => {
          this.$bvToast.toast(res.msg, {
            title: 'UPS Facturas',
            variant: 'success',
            solid: true,
          })
          this.cargarFacturas()
        })
        .catch(function() {})
    },
    ejecutarAnalisis() {
      if (!this.analisisTipo) return

      this.analizando = true
      var params = '?tipo=' + this.analisisTipo
      if (this.analisisFacturaId) params += '&facturaId=' + this.analisisFacturaId
      if (this.analisisDesde) params += '&desde=' + this.analisisDesde
      if (this.analisisHasta) params += '&hasta=' + this.analisisHasta

      axios
        .get('/ups/analisis' + params)
        .then(function(response) { return response.data })
        .then((res) => {
          if (this.analisisTipo === 'zona') {
            res = this.ordenarPorPais(res)
            this.construirChartZona(res)
          }

          if (this.analisisTipo === 'categoria') {
            this.totalEnviosUnicos = res.totalEnvios || 0
            res = res.categorias || []
            if (res.length > 0) this.construirChartCategoria(res)
          }

          this.analisisResultados = res

          if (this.analisisTipo === 'temporal' && res.length > 0) {
            this.construirChart(res)
          }

          this.analizando = false
        })
        .catch(() => {
          this.analizando = false
        })
    },
    construirChart(datos) {
      var meses = datos.map(function(d) { return d.mes })
      var costes = datos.map(function(d) { return parseFloat(d.costeTotal).toFixed(2) })
      var fletes = datos.map(function(d) { return parseFloat(d.costeFlete).toFixed(2) })
      var combustible = datos.map(function(d) { return parseFloat(d.costeCombustible).toFixed(2) })

      this.chartSeries = [
        { name: 'Coste Total', data: costes },
        { name: 'Flete', data: fletes },
        { name: 'Combustible', data: combustible },
      ]

      this.chartOptions = {
        chart: {
          type: 'area',
          toolbar: { show: true },
          fontFamily: 'Montserrat, sans-serif',
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: meses,
          labels: { style: { fontSize: '12px' } },
        },
        yaxis: {
          title: { text: 'Coste (€)', style: { fontSize: '13px' } },
          labels: {
            formatter: function(val) { return parseFloat(val).toFixed(0) + ' €' },
          },
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
          },
        },
        colors: ['#7367f0', '#28c76f', '#ff9f43'],
        tooltip: {
          y: {
            formatter: function(val) {
              return parseFloat(val).toFixed(2) + ' €'
            },
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'right',
        },
        grid: {
          borderColor: '#f1f1f1',
          padding: { left: 10, right: 10 },
        },
      }
    },
    construirChartZona(datos) {
      // Agrupar costes de envío por país (solo envíos)
      var paisesEnvio = {}
      datos.forEach(function(r) {
        if (r.tipoEnvio !== 'envio') return
        paisesEnvio[r.paisDestino] = parseFloat(r.costeTotal) || 0
      })

      // Ordenar por coste y tomar top 10
      var ordenados = Object.keys(paisesEnvio).sort(function(a, b) {
        return paisesEnvio[b] - paisesEnvio[a]
      })

      var top = ordenados.slice(0, 10)
      var topCostes = top.map(function(p) { return parseFloat(paisesEnvio[p].toFixed(2)) })

      // Bar chart — top 10
      this.chartZonaSeries = [{ name: 'Coste', data: topCostes }]
      this.chartZonaOptions = {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'Montserrat, sans-serif',
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '65%',
            borderRadius: 4,
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) { return parseFloat(val).toFixed(0) + ' €' },
          style: { fontSize: '12px', fontWeight: 600 },
          offsetX: 5,
        },
        xaxis: {
          categories: top,
          labels: {
            formatter: function(val) { return parseFloat(val).toFixed(0) + ' €' },
          },
        },
        colors: ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8', '#a855f7', '#f472b6', '#38bdf8', '#4ade80', '#82868b'],
        legend: { show: false },
        tooltip: {
          y: { formatter: function(val) { return parseFloat(val).toFixed(2) + ' €' } },
        },
        title: {
          text: 'Top 10 Países por Coste',
          style: { fontSize: '14px', fontWeight: 600, color: '#5e5873' },
        },
      }

      // Donut — top 10 + "Otros"
      var donutLabels = top.slice()
      var donutValues = topCostes.slice()
      var resto = ordenados.slice(10)
      if (resto.length > 0) {
        var otrosTotal = 0
        resto.forEach(function(p) { otrosTotal += paisesEnvio[p] })
        donutLabels.push('Otros (' + resto.length + ')')
        donutValues.push(parseFloat(otrosTotal.toFixed(2)))
      }

      this.chartZonaDonutSeries = donutValues
      this.chartZonaDonutOptions = {
        chart: {
          type: 'donut',
          fontFamily: 'Montserrat, sans-serif',
        },
        labels: donutLabels,
        colors: ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8', '#a855f7', '#f472b6', '#38bdf8', '#4ade80', '#82868b', '#d1d5db'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) { return parseFloat(val).toFixed(1) + '%' },
        },
        tooltip: {
          y: { formatter: function(val) { return parseFloat(val).toFixed(2) + ' €' } },
        },
        title: {
          text: 'Distribución por País',
          style: { fontSize: '14px', fontWeight: 600, color: '#5e5873' },
        },
      }
    },
    construirChartCategoria(datos) {
      var top = datos.slice(0, 10)
      var labels = top.map(function(d) { return d.descripcionCargo || d.clasificacionCargo })
      var costes = top.map(function(d) { return parseFloat(parseFloat(d.costeTotal).toFixed(2)) })

      // Bar chart
      this.chartCategoriaSeries = [{ name: 'Coste', data: costes }]
      this.chartCategoriaOptions = {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'Montserrat, sans-serif',
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '65%',
            borderRadius: 4,
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) { return parseFloat(val).toFixed(0) + ' €' },
          style: { fontSize: '12px', fontWeight: 600 },
          offsetX: 5,
        },
        xaxis: {
          categories: labels,
          labels: {
            formatter: function(val) { return parseFloat(val).toFixed(0) + ' €' },
          },
        },
        colors: ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8', '#a855f7', '#f472b6', '#38bdf8', '#4ade80', '#82868b'],
        legend: { show: false },
        tooltip: {
          y: { formatter: function(val) { return parseFloat(val).toFixed(2) + ' €' } },
        },
        title: {
          text: 'Top 10 Categorías por Coste',
          style: { fontSize: '14px', fontWeight: 600, color: '#5e5873' },
        },
      }

      // Donut
      var donutLabels = labels.slice()
      var donutValues = costes.slice()
      var resto = datos.slice(10)
      if (resto.length > 0) {
        var otrosTotal = 0
        resto.forEach(function(d) { otrosTotal += parseFloat(d.costeTotal) || 0 })
        donutLabels.push('Otros (' + resto.length + ')')
        donutValues.push(parseFloat(otrosTotal.toFixed(2)))
      }

      this.chartCategoriaDonutSeries = donutValues
      this.chartCategoriaDonutOptions = {
        chart: {
          type: 'donut',
          fontFamily: 'Montserrat, sans-serif',
        },
        labels: donutLabels,
        colors: ['#7367f0', '#28c76f', '#ff9f43', '#ea5455', '#00cfe8', '#a855f7', '#f472b6', '#38bdf8', '#4ade80', '#82868b', '#d1d5db'],
        legend: {
          position: 'bottom',
          fontSize: '13px',
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) { return parseFloat(val).toFixed(1) + '%' },
        },
        tooltip: {
          y: { formatter: function(val) { return parseFloat(val).toFixed(2) + ' €' } },
        },
        title: {
          text: 'Distribución por Categoría',
          style: { fontSize: '14px', fontWeight: 600, color: '#5e5873' },
        },
      }
    },
    ordenarPorPais(datos) {
      // Agrupar por país: envío primero, devolución después
      var paises = {}
      datos.forEach(function(r) {
        if (!paises[r.paisDestino]) paises[r.paisDestino] = { envio: null, devolucion: null }
        if (r.tipoEnvio === 'envio') paises[r.paisDestino].envio = r
        else paises[r.paisDestino].devolucion = r
      })

      // Ordenar países por coste de envío (mayor primero)
      var keys = Object.keys(paises).sort(function(a, b) {
        var costeA = paises[a].envio ? parseFloat(paises[a].envio.costeTotal) : 0
        var costeB = paises[b].envio ? parseFloat(paises[b].envio.costeTotal) : 0
        return costeB - costeA
      })

      // Reconstruir: envío, luego devolución
      var resultado = []
      keys.forEach(function(pais) {
        if (paises[pais].envio) resultado.push(paises[pais].envio)
        if (paises[pais].devolucion) resultado.push(paises[pais].devolucion)
      })
      return resultado
    },
    formatFecha(fecha) {
      if (!fecha) return '-'
      var d = new Date(fecha)
      if (isNaN(d.getTime())) return fecha
      var day = ('0' + d.getDate()).slice(-2)
      var month = ('0' + (d.getMonth() + 1)).slice(-2)
      return day + '/' + month + '/' + d.getFullYear()
    },
    toggleDetalle(paisItem, cat, idx) {
      // Toggle
      if (cat._open) {
        this.$set(cat, '_open', false)
        return
      }

      // Cerrar otros del mismo país
      if (paisItem._categorias) {
        paisItem._categorias.forEach(function(c) { if (c._open) c._open = false })
      }

      this.$set(cat, '_open', true)

      if (cat._trackings) return // Ya cargados

      this.$set(cat, '_loadingDetalle', true)

      var params = '?tipo=detalle'
      params += '&pais=' + encodeURIComponent(paisItem.paisDestino)
      if (paisItem.tipoEnvio) params += '&tipoEnvio=' + paisItem.tipoEnvio
      params += '&clasificacion=' + encodeURIComponent(cat.clasificacionCargo)
      params += '&descripcion=' + encodeURIComponent(cat.descripcionCargo || '')
      if (this.analisisFacturaId) params += '&facturaId=' + this.analisisFacturaId
      if (this.analisisDesde) params += '&desde=' + this.analisisDesde
      if (this.analisisHasta) params += '&hasta=' + this.analisisHasta

      axios
        .get('/ups/analisis' + params)
        .then(function(response) { return response.data })
        .then((res) => {
          this.$set(cat, '_trackings', res)
          this.$set(cat, '_loadingDetalle', false)
        })
        .catch(() => {
          this.$set(cat, '_loadingDetalle', false)
        })
    },
    onRowClick(item) {
      if (this.analisisTipo !== 'zona') return
      if (!item.paisDestino) return

      // Toggle: si ya está abierto, cerrar
      if (item._showDetails) {
        this.$set(item, '_showDetails', false)
        return
      }

      // Cerrar otros abiertos
      this.analisisResultados.forEach(function(r) {
        if (r._showDetails) r._showDetails = false
      })

      this.$set(item, '_showDetails', true)
      this.$set(item, '_loading', true)

      var params = '?tipo=pais&pais=' + item.paisDestino
      if (item.tipoEnvio) params += '&tipoEnvio=' + item.tipoEnvio
      if (this.analisisDesde) params += '&desde=' + this.analisisDesde
      if (this.analisisHasta) params += '&hasta=' + this.analisisHasta

      axios
        .get('/ups/analisis' + params)
        .then(function(response) { return response.data })
        .then((res) => {
          this.$set(item, '_categorias', res)
          this.$set(item, '_loading', false)
        })
        .catch(() => {
          this.$set(item, '_loading', false)
        })
    },
    rowClass(item) {
      if (this.analisisTipo === 'zona' && item) {
        return item._showDetails ? 'table-row-selected cursor-pointer' : 'cursor-pointer'
      }
      return ''
    },
    varianteCargo(clasificacion) {
      var map = {
        'FRT': 'light-primary',
        'FSC': 'light-warning',
        'ACC': 'light-info',
        'TAX': 'light-danger'
      }
      return map[clasificacion] || 'light-secondary'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.resumen-card {
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
}

.tabla-facturas >>> tbody tr:hover {
  background-color: rgba(115, 103, 240, 0.04) !important;
}

>>> .table-row-selected {
  background-color: rgba(115, 103, 240, 0.08) !important;
  border-left: 3px solid #7367f0;
}

>>> .cursor-pointer {
  cursor: pointer;
}

.table-header-custom {
  background-color: #f8f8f8 !important;
}

.table-header-custom th {
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  font-weight: 600;
  border-bottom: 2px solid #ebe9f1 !important;
  padding: 10px 14px !important;
}

.chart-container {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.label-custom {
  font-size: 12px;
  font-weight: 600;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  display: block;
}

.btn-icon {
  padding: 4px 8px;
  border-radius: 6px;
}

.desglose-container {
  padding: 8px 16px 8px 32px;
  background: #f8f8f8;
  border-left: 3px solid #7367f0;
  max-width: 700px;
}

.desglose-row {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #ebe9f1;
  font-size: 13px;
}

.desglose-row-active {
  background: rgba(115, 103, 240, 0.04);
}

.detalle-trackings {
  padding: 4px 0 8px 50px;
  max-width: 700px;
}

.detalle-tracking-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
  color: #6e6b7b;
  border-bottom: 1px dashed #ebe9f1;
  gap: 8px;
}

.detalle-tracking-row:last-child {
  border-bottom: none;
}

.detalle-tracking-num {
  width: 220px;
  flex-shrink: 0;
  font-family: monospace;
  font-size: 11px;
}

.detalle-tracking-ref {
  width: 150px;
  flex-shrink: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detalle-tracking-fecha {
  width: 90px;
  flex-shrink: 0;
  text-align: right;
}

.detalle-tracking-peso {
  width: 60px;
  flex-shrink: 0;
  text-align: right;
}

.detalle-tracking-coste {
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  font-weight: 600;
  color: #7367f0;
}

.desglose-badge {
  width: 50px;
  flex-shrink: 0;
}

.desglose-desc {
  width: 280px;
  flex-shrink: 1;
  color: #6e6b7b;
  padding-right: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desglose-envios {
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  color: #6e6b7b;
  padding-right: 12px;
}

.desglose-coste {
  width: 90px;
  flex-shrink: 0;
  text-align: right;
  font-weight: 600;
  color: #7367f0;
}
</style>
